<template>
  <v-dialog
    v-model="show"
    max-width="500px"
  >
    <v-card class="content">
      <div class="d-flex justify-space-between">
        <v-card-title class="title">{{ mode === dialogModes.create ? 'Add Time' : 'Edit Time' }}</v-card-title>
        <v-btn
          class="mr-2 mt-2"
          icon
          small
          @click="show = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <v-divider></v-divider>
      <v-card-text class="px-4 pb-0">
        <v-card-subtitle class="pt-2 pb-2 px-0 subtitle">
          Task
        </v-card-subtitle>
        <div class="file-input">
          <v-text-field
            v-model="displayedValue"
            label="Select or Search"
            solo
            hide-details
            :readonly="!!currentSelectedTasks.length"
            @input="toggleTree('input')"
          >
            <template v-slot:prepend-inner>
              <v-icon
                v-if="currentSelectedTasks.length && !currentSelectedTasks[0].Is_Folder"
                color="success"
              >
                mdi-file
              </v-icon>
            </template>
            <template v-slot:append>
              <v-btn
                v-if="displayedValue"
                rounded
                icon
                color="primary"
                @click="toggleTree('clear')"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
              <v-btn
                rounded
                icon
                color="primary"
                @mouseup.stop @click.prevent.stop="toggleTree('expand')"
              >
                <v-icon
                  size="30"
                  :class="{ 'rotate': isArrowUp }"
                >
                  mdi-chevron-down
                </v-icon>
              </v-btn>
            </template>
          </v-text-field>
          <v-expansion-panels
            v-show="showTree"
            class="panels"
            multiple
            accordion
            v-model="expandedPanels"
          >
            <v-expansion-panel
              v-show="showProject(project)"
              v-for="(project, projectKey) in filteredProjectsAndTasks"
              :key="projectKey"
            >
              <v-expansion-panel-header>
                {{ project.Project_Name }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-treeview
                  :active="currentSelectedTasks"
                  :items="project.Tasks"
                  :open="expandedNodes[project.Project_ID] || []"
                  @update:open="val => $set(expandedNodes, project.Project_ID, val)"
                  item-key="Task_ID"
                  item-text="Name"
                  activatable
                  return-object
                  dense
                  open-on-click
                  @update:active="selectedTask"
                >
                  <template v-slot:prepend="{ item, open, active }">
                    <v-icon
                      v-if="item.Is_Folder"
                      color="primary"
                      @click="active ? $event.stopPropagation() : null"
                    >
                      {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
                    </v-icon>
                    <v-icon
                      v-else
                      color="success"
                      @click="active ? $event.stopPropagation() : null"
                    >
                      mdi-file
                    </v-icon>
                  </template>
                  <template v-slot:label="{ item, active }">
                    <div class="d-flex justify-space-between align-center">
                      <div @click="active ? $event.stopPropagation() : null">
                        {{ item.Name }}
                      </div>
                      <v-tooltip v-if="item.Can_Create_Tasks" bottom color="success">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn v-bind="attrs" v-on="on" icon color="success" @click.stop="showTaskDialog(item.Project_ID, item.Task_ID, item.root_task_id)">
                            <v-icon>mdi-plus-circle</v-icon>
                          </v-btn>
                        </template>
                        <span>Add task</span>
                      </v-tooltip>
                    </div>
                  </template>
                </v-treeview>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
        <div class="picker-est-block">
          <div>
            <v-card-subtitle class="pt-4 pb-2 px-0 subtitle">
              Date
            </v-card-subtitle>
            <DatePicker
              dense
              v-model="currentReport.Start_Date"
            />
          </div>
          <div class="est-block">
            <v-card-subtitle class="pt-4 pb-2 px-0 subtitle">
              Est./Rep.
            </v-card-subtitle>
            <div
              v-if="dialogModes.update === mode"
              class="align-self-center"
            >
              {{ currentReport.EstRep }}
            </div>
            <div
              v-else-if="dialogModes.create === mode && currentSelectedTasks.length"
              class="align-self-center"
            >
              {{
                currentSelectedTasks[0].Estimate.toFixed(2) + '/' + currentSelectedTasks[0].Total_Paid_Dur.toFixed(2)
              }}
            </div>
          </div>
        </div>
        <div>
          <v-card-subtitle class="pt-4 pb-2 px-0 subtitle">
            Time
          </v-card-subtitle>
        </div>
        <div class="d-flex justify-space-between">
          <TimePicker
            v-model="currentReport.Reports"
            label="Reported"
            dense
            class="mr-2"
          />
          <TimePicker
            v-model="currentReport.Paid_Reports"
            label="Paid"
            dense
            class="ml-2"
          />
        </div>
        <v-card-subtitle class="pt-4 pb-2 px-0 subtitle">
          Activity Type
        </v-card-subtitle>
        <v-select
          v-model="currentReport.Activity_Type"
          :items="activityTypes"
          item-text="Description"
          item-value="Activity_Type"
          outlined
          dense
          hide-details
        ></v-select>
        <v-card-subtitle class="pt-4 pb-2 px-0 subtitle d-flex justify-space-between align-center">
          <span>Description</span>
          <BadWords
            v-show="!showTree"
            :description="currentReport.Description"
            :hours="currentReport.Reports"
            :bad-words="badWords"
          />
        </v-card-subtitle>
        <div class="description-container">
          <DescriptionWithSpellCheck
            v-model="currentReport.Description"
            :rows="6"
            auto-grow
          />
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="justify-space-between">
        <v-btn
          small
          class="action-btn"
          color="primary"
          outlined
          @click="show = false"
        >
          cancel
        </v-btn>
        <v-btn
          small
          :disabled="!checkForm"
          class="action-btn"
          color="primary"
          @click="saveReport"
        >
          save
        </v-btn>
      </v-card-actions>
    </v-card>
    <TaskDialog
      v-if="showTask"
      createFromFolder
    />
  </v-dialog>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import { sharedReportingMixin } from '@/mixins/sharedReportingMixin'
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'

export default {
  name: 'ManageReportingDialog',
  mixins: [sharedReportingMixin],
  components: {
    DatePicker: () => import('@/components/global/DatePicker.vue'),
    TaskDialog: () => import('@/components/tasks_editor/TaskDialog.vue'),
    TimePicker: () => import('@/components/global/TimePicker.vue'),
    DescriptionWithSpellCheck: () => import('@/components/reporting/DescriptionWithSpellCheck.vue'),
    BadWords: () => import('@/components/global/BadWords.vue')
  },
  props: {
    value: {
      required: false,
      type: Boolean,
      default: false
    },
    mode: {
      required: false,
      type: String,
      default: 'create'
    },
    report: {
      required: true,
      type: Object
    }
  },
  data () {
    return {
      showTree: false,
      search: '',
      isArrowUp: false,
      isInputsValid: {
        Reports: false,
        Paid_Reports: false
      },
      currentSelectedTasks: [],
      currentReport: { ...this.report },
      listOfProjectsAndTasks: [],
      filteredProjectsAndTasks: [],
      expandedPanels: [],
      expandedNodes: {},
      dialogModes: {
        create: 'create',
        update: 'update'
      }
    }
  },
  async mounted () {
    if (this.dialogModes.update === this.mode) {
      this.currentReport.Reports = this.reportHoursToTime(this.report.Reports)
      this.currentReport.Paid_Reports = this.reportHoursToTime(this.report.Paid_Reports)
    }

    this.getEmployeeList()
    this.getBadWords()

    await this.getProjectsAndTasks()

    if (this.dialogModes.update === this.mode && this.listOfProjectsAndTasks.length) {
      const foundProject = this.listOfProjectsAndTasks.find(el => el.Project_Name === this.report.Project_Name)
      if (foundProject) {
        this.findTaskByReport(foundProject.Tasks)
      }
    } else if (this.dialogModes.create === this.mode) {
      setTimeout(() => {
        this.checkAndSelectDefault()
      }, 150)
    }
  },
  computed: {
    ...mapState({
      badWordsFromStore: state => state.badWords || [],
      activityTypes: state => state.activityTypes,
      employeeRoles: state => state.employeeRoles,
      user: state => state.user
    }),
    settings () {
      return this.$store.getters.getSettings
    },
    checkForm () {
      return !!this.currentReport.Reports && !!this.currentReport.Paid_Reports &&
        !!this.currentReport.Description && !!this.currentReport.Activity_Type &&
        this.currentSelectedTasks.length && !!this.currentReport.Start_Date
    },
    displayedValue: {
      get () {
        if (this.currentSelectedTasks.length > 0) {
          return this.currentSelectedTasks[0].Name
        }
        return this.search
      },
      set (value) {
        if (this.currentSelectedTasks.length) {
          this.currentSelectedTasks = []
        }
        this.search = value
        this.updateExpandedValues()
      }
    },
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    showTask () {
      return this.$store.getters.showTaskDialog
    }
  },
  methods: {
    checkAndSelectDefault () {
      if (this.settings?.defaultTask?.Task_ID && this.listOfProjectsAndTasks.length > 0 && this.currentSelectedTasks.length === 0) {
        this.findAndSelectDefaultTask(this.listOfProjectsAndTasks)
      }
    },
    findAndSelectDefaultTask (projects) {
      const targetId = Number(this.settings.defaultTask.Task_ID)
      let foundTaskObj = null
      let parentIds = []

      const search = (tasks, path) => {
        for (const task of tasks) {
          if (Number(task.Task_ID) === targetId) {
            foundTaskObj = task
            parentIds = path
            return true
          }
          if (task.children && search(task.children, [...path, task.Task_ID])) return true
        }
        return false
      }

      for (const project of projects) {
        if (search(project.Tasks, [])) break
      }

      if (foundTaskObj) {
        this.currentSelectedTasks = [foundTaskObj]
        this.$set(this.expandedNodes, foundTaskObj.Project_ID, parentIds)
        const pIndex = this.filteredProjectsAndTasks.findIndex(p => p.Project_ID === foundTaskObj.Project_ID)
        if (pIndex !== -1) {
          this.expandedPanels = [pIndex]
        }
      }
    },
    canReport (projectName) {
      const excludedProjects = ['DC Vacations', 'DC Sick-Leave']
      const isFinancier = this.employeeRoles.financier.val

      if (!isFinancier && excludedProjects.includes(projectName)) {
        this.$store.dispatch('setError', { errorMsg: 'Only Financier role can Save/Update this project report' })
        return false
      }
      return true
    },
    showTaskDialog (projectID, TaskID, rootTaskID) {
      const defaultTask = {
        Assigned_To: this.user.employee_id,
        Budget_SubType_ID: null,
        Description: '',
        Estimate: 0,
        Is_Folder: false,
        Name: '',
        Parent_Task_ID: TaskID,
        Project_ID: projectID,
        Reported: 0,
        Show_IN_Report: true,
        Start_Date: formatLocalDate(new Date()),
        Status: 'Open',
        Task_ID: null,
        Redmine_Issue_ID: null,
        TestTrack_ID: null,
        root_task_id: rootTaskID
      }
      this.$store.dispatch('setCurrentTaskData', { taskData: defaultTask })
      this.$store.dispatch('showTaskDialog', { show: true })
    },
    async getEmployeeList () {
      this.axios.get('/api/employeelist', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.$store.dispatch('setEmployeeList', { employeeList: response.data })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    inputInvalid (inputName) {
      this.isInputsValid[inputName] = false
    },
    inputValid (inputValue) {
      this.currentReport[inputValue.fieldName] = inputValue.convertedTime
      this.isInputsValid[inputValue.fieldName] = true
    },
    parseTimeToHours (timeStr) {
      if (!timeStr) return 0
      const [hours, minutes] = timeStr.split(':').map(Number)
      return hours + (minutes / 60)
    },
    saveReport () {
      if (this.checkForm) {
        const reportsHours = this.parseTimeToHours(this.currentReport.Reports)
        const paidHours = this.parseTimeToHours(this.currentReport.Paid_Reports)

        if (this.dialogModes.create === this.mode && !this.currentReport.Reporting_ID) {
          this.currentReport.Task_ID = this.currentSelectedTasks[0].Task_ID
          this.currentReport.Project_ID = this.currentSelectedTasks[0].Project_ID
          this.currentReport.Project_Name = this.currentSelectedTasks[0].Project_Name

          const reportWithModifyTime = {
            ...this.currentReport,
            Reports: this.reportHoursToTime(reportsHours),
            Paid_Reports: this.reportHoursToTime(paidHours)
          }
          if (this.canReport(reportWithModifyTime.Project_Name)) this.postReport(reportWithModifyTime)
        } else if (this.dialogModes.update === this.mode && this.currentReport.Reporting_ID) {
          const reportItemsToUpdate = {
            Task_ID: this.currentSelectedTasks[0].Task_ID,
            Project_ID: this.currentSelectedTasks[0].Project_ID,
            Start_Date: this.currentReport.Start_Date,
            Reports: this.reportHoursToTime(reportsHours),
            Paid_Reports: this.reportHoursToTime(paidHours),
            Description: this.currentReport.Description,
            Activity_Type: this.currentReport.Activity_Type,
            Project_Name: this.currentReport.Project_Name
          }
          if (this.canReport(reportItemsToUpdate.Project_Name)) this.putReport(reportItemsToUpdate, this.currentReport.Reporting_ID)
        }
      }
    },
    findTaskByReport (tasks) {
      if (tasks && tasks.length) {
        tasks.find(el => {
          if (el.Task_ID === this.currentReport.Task_ID) {
            this.currentSelectedTasks.push(el)
            return
          }
          if (el.children && el.children.length) {
            return this.findTaskByReport(el.children)
          }
        })
      }
    },
    toggleTree (eventType) {
      if (eventType === 'clear') {
        this.currentSelectedTasks = []
        this.displayedValue = ''
        this.updateExpandedValues()
      } else if (eventType === 'input' && !this.showTree) {
        this.showTree = !this.showTree
        this.isArrowUp = !this.isArrowUp
        this.updateExpandedValues()
      } else if (eventType === 'expand') {
        this.showTree = !this.showTree
        this.isArrowUp = !this.isArrowUp
        this.updateExpandedValues()
      }
    },
    selectedTask (e) {
      if (e.length && !e[0].Is_Folder) {
        this.currentSelectedTasks = []
        this.currentSelectedTasks.push(e[0])
        this.showTree = !this.showTree
        this.isArrowUp = !this.isArrowUp
        this.clearExpandedValues()
      }
    },
    reportHoursToTime (value) {
      if (value < 0 || value > 23.5) {
        return
      }
      const duration = moment.duration(value, 'hours')
      const hours = duration.hours()
      const minutes = duration.minutes()
      const formattedHours = hours.toString().padStart(2, '0')
      const formattedMinutes = minutes.toString().padStart(2, '0')
      return `${formattedHours}:${formattedMinutes}`
    },
    async getProjectsAndTasks () {
      await this.axios.get('/api/tasks/reps/list-of-projects-and-tasks', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.filteredProjectsAndTasks = response.data
        this.listOfProjectsAndTasks = response.data
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    postReport (report) {
      this.axios.post('/api/day_reports', report, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.$emit('reportUpdate', this.currentReport.Start_Date)
        this.$store.dispatch('setSuccess', { successMsg: 'Reporting successfully saved' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    putReport (items, id) {
      this.axios.put(`/api/day_reports/${id}`, items, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        if (response.data[0].AffectedRows === 0) {
          this.$store.dispatch('setError', { errorMsg: 'Something went wrong' })
          return
        }
        this.$emit('reportUpdate')
        this.$store.dispatch('setSuccess', { successMsg: 'Reporting successfully updated' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    clearExpandedValues () {
      this.expandedPanels = []
      this.expandedNodes = {}
    },
    updateExpandedValues () {
      this.clearExpandedValues()
      this.filteredProjectsAndTasks = JSON.parse(JSON.stringify(this.listOfProjectsAndTasks))

      // Restore path to selected task in open nodes
      if (this.currentSelectedTasks.length) {
        const selectedTask = this.currentSelectedTasks[0]
        const projectId = selectedTask.Project_ID
        const project = this.filteredProjectsAndTasks.find(p => p.Project_ID === projectId)
        if (project) {
          const parentNodes = []
          const findPath = (tasks) => {
            for (const task of tasks) {
              if (task.Task_ID === selectedTask.Task_ID) return true
              if (task.children && task.children.length) {
                if (findPath(task.children)) {
                  parentNodes.push(task)
                  return true
                }
              }
            }
            return false
          }
          findPath(project.Tasks)
          this.$set(this.expandedNodes, projectId, parentNodes)

          const pIndex = this.filteredProjectsAndTasks.findIndex(p => p.Project_ID === projectId)
          if (pIndex !== -1) {
            this.expandedPanels.push(pIndex)
          }
        }
      }

      if (!this.search) return

      this.filteredProjectsAndTasks = JSON.parse(JSON.stringify(this.listOfProjectsAndTasks))
        .map(project => {
          if (this.showProject(project)) {
            const filteredProject = { ...project }
            if (!project.Project_Name.toLowerCase().includes(this.search.toLowerCase())) {
              filteredProject.Tasks = this.filterTasks(filteredProject.Tasks, this.search.toLowerCase())
            }
            return filteredProject
          }
          return null
        })
        .filter(project => project !== null)

      this.filteredProjectsAndTasks.forEach((project, index) => {
        if (this.showProject(project)) {
          this.expandedPanels.push(index)
          this.expandTaskFolders(project.Tasks, this.search.toLowerCase(), project.Project_ID)
        }
      })
    },
    filterTasks (tasks, search) {
      return tasks.reduce((filtered, task) => {
        const isTaskMatch = task.Name.toLowerCase().includes(search)
        const filteredChildren = task.children ? this.filterTasks(task.children, search) : []
        if (!task.Is_Folder && (isTaskMatch || filteredChildren.length > 0)) {
          filtered.push({
            ...task,
            children: filteredChildren
          })
        } else if (task.Is_Folder && filteredChildren.length > 0) {
          filtered.push({
            ...task,
            children: filteredChildren
          })
        }

        return filtered
      }, [])
    },
    expandTaskFolders (tasks, search, projectId) {
      tasks.forEach(task => {
        if (task.Name.toLowerCase().includes(search)) {
          if (task.Parent_Task_ID) this.expandParentFolders(task.Parent_Task_ID, projectId)
        }
        if (task.children) this.expandTaskFolders(task.children, search, projectId)
      })
    },
    expandParentFolders (parentId, projectId) {
      if (!this.expandedNodes[projectId]) {
        this.$set(this.expandedNodes, projectId, [])
      }
      this.expandedNodes[projectId].push({ Task_ID: parentId })
      const parentTask = this.findTaskById(parentId)
      if (parentTask && parentTask.Parent_Task_ID) this.expandParentFolders(parentTask.Parent_Task_ID, projectId)
    },
    findTaskById (taskId) {
      let foundTask = null
      const searchTask = tasks => {
        for (const task of tasks) {
          if (task.Task_ID === taskId) {
            foundTask = task
            break
          }
          if (task.children) searchTask(task.children)
        }
      }
      this.filteredProjectsAndTasks.forEach(project => {
        searchTask(project.Tasks)
      })
      return foundTask
    },
    showProject (project) {
      const searchLower = this.search.toLowerCase()
      return project.Project_Name.toLowerCase().includes(searchLower) ||
             this.containsTask(project.Tasks, searchLower)
    },
    containsTask (tasks, search) {
      for (const task of tasks) {
        if (task.Name.toLowerCase().includes(search)) return true
        if (task.children && this.containsTask(task.children, search)) return true
      }
      return false
    }
  },
  watch: {
    'currentReport.Reports': function (newVal) {
      if (newVal && !this.currentReport.Paid_Reports) {
        this.currentReport.Paid_Reports = newVal
      }
    },
    showTask: {
      async handler (val) {
        if (!val) await this.getProjectsAndTasks()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.content {
  overflow: auto;
  max-height: 90vh;
}
.title {
  color: #6D6D6D;
  font-weight: 500 !important;
  font-size: 18px !important;
}
.subtitle {
  color: #6D6D6D;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
}
.rotate {
  transform: rotate(-180deg);
  transition: transform 0.3s ease-in-out;
}
.file-input {
  position: relative;
}
.panels {
  position: absolute;
  top: 50px;
  max-height: 475px;
  overflow-y: auto;
  -webkit-box-shadow: 0 5px 19px -7px rgba(135, 135, 135, 0.47);
  box-shadow: 0 5px 19px -7px rgba(135, 135, 135, 0.47);
}
.picker-est-block {
  display: flex;
  justify-content: space-between;
}
.est-block {
  width: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between
}
.reported-paid-block {
  display: flex;
}
.report-title {
  width: 50%;
}
.action-btn {
  width: 80px;
}
.description-container {
  position: relative;
  min-height: 200px;
}
</style>
